import React from 'react';
import { StatCard } from '../../../components/ui';
import { SkeletonBlock } from '../../../components/skeleton/skeleton';
import { formatCurrency } from '../../../utils/baseUtils';
import { COPY } from './connect-finance-copy';

const plural = (count, noun) => count + ' ' + noun + (count === 1 ? '' : 's');

// This is an arithmetic statement, not a set of six equal facts: Gross and
// Net are the two figures a restaurant came for, the three fees between
// them are the deduction list that explains the difference, and Payouts is
// a different fact (money that already left) that never belonged in the
// chain. The card grid still reads only keys that exist on the adapted
// summary and never derives one figure from another: the restaurant must
// not be able to reconstruct Stripe's buy rate from anything shown here,
// and applicationFees (which the API sends to everyone, backend TODO C1) is
// an admin-only fact for the same reason.
//
// On some periods the three listed fees don't close the equation by
// themselves: Stripe rejects an application fee larger than the order it's
// drawn from, so the fee engine caps it and Thisway absorbs the rest. The
// deduction list gains a row only on those periods — feesNotCollected reads
// as a credit back to Net, otherDeductions as one more thing taken out — so
// an ordinary period, where both are zero, is untouched.
const ConnectFinanceSummary = ({ summary, viewer, loading = false }) => {
  const s = summary || {};
  const money = (value) => formatCurrency(value || 0);
  const showBuyRate = viewer === 'admin' && s.includesBuyRate === true;

  const deductions = [
    // The customer paid this on top of the food; it passes through to
    // Thisway, so it is the first line in the deduction list on the way
    // from Gross to Net.
    { key: 'platformFee', label: 'Platform fee', value: s.platformFees, hint: 'Paid by the customer' },
    { key: 'cardFee', label: 'Card fee', value: s.cardFee },
    { key: 'thiswayFee', label: 'Thisway fee', value: s.thiswayFee },
  ];
  // Both rows are omitted whenever their figure is zero (the ordinary
  // case), so an unaffected period renders exactly as it always has.
  if (s.otherDeductions) {
    deductions.push({
      key: 'otherDeductions',
      label: COPY.summaryRows.otherDeductionsLabel,
      hint: COPY.summaryRows.otherDeductionsHint,
      value: s.otherDeductions,
    });
  }
  if (s.feesNotCollected) {
    // Not a fourth deduction: the fee was capped at the order's own value,
    // so this is money the restaurant kept, and it has to read as an
    // addition back rather than one more thing taken out.
    deductions.push({
      key: 'feesNotCollected',
      label: COPY.summaryRows.feesNotCollectedLabel,
      hint: COPY.summaryRows.feesNotCollectedHint,
      value: s.feesNotCollected,
      credit: true,
    });
  }

  const reconciliation = [];
  if (showBuyRate) {
    if ('stripeProcessingFee' in s) {
      reconciliation.push({ key: 'stripeFee', label: 'Stripe processing fee', value: money(s.stripeProcessingFee) });
    }
    if ('cardFeeMargin' in s) {
      reconciliation.push({ key: 'margin', label: 'Card-fee margin', value: money(s.cardFeeMargin) });
    }
    reconciliation.push({ key: 'appFees', label: 'Application fees', value: money(s.applicationFees) });
    reconciliation.push({ key: 'intl', label: 'International cards', value: String(s.internationalCount || 0) });
  }

  return (
    <div className="space-y-6">
      {/* Gross - fees = Net. Side by side once there's room to hold an
          equation (lg+, the same breakpoint the old grid used for its third
          column); stacked top-to-bottom, in the same reading order, below
          that — a phone and a narrow tablet both get the vertical waterfall,
          since three cards and two operators genuinely don't fit side by
          side before lg. Either way the deduction list sits between the two
          answers, never after them. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        <StatCard
          className="lg:flex-1"
          label="Gross"
          value={money(s.gross)}
          hint={plural(s.paymentCount || 0, 'payment')}
          loading={loading}
          uppercaseLabel={false}
        />
        <Operator symbol="−" />
        <DeductionsCard rows={deductions} loading={loading} />
        {/* A refund is money that went back to the customer, not a fee
            Thisway charged, so it gets its own place in the equation instead
            of a fourth line inside a card titled Fees. Drawn only when the
            period actually has one — the same rule the order statement uses
            for its optional columns, so an ordinary week still reads
            Gross − Fees = Net. */}
        {s.refundCount > 0 ? (
          <>
            <Operator symbol="−" />
            <StatCard
              className="lg:flex-1"
              label="Refunds"
              value={money(s.refundAmount)}
              hint={plural(s.refundCount, 'refund')}
              loading={loading}
              uppercaseLabel={false}
            />
          </>
        ) : null}
        <Operator symbol="=" />
        <StatCard
          className="lg:flex-1"
          label="Net to restaurant"
          value={money(s.restaurantNet)}
          valueClassName="text-blue-600 dark:text-sky-400"
          loading={loading}
          uppercaseLabel={false}
        />
      </div>

      {/* Payouts is money that has already left, not part of this period's
          arithmetic — a border and its own row keep it from reading as a
          seventh line of the equation above. */}
      <div className="border-t border-slate-200 pt-5 dark:border-slate-700">
        <StatCard
          className="max-w-xs"
          label="Payouts"
          value={money(s.payoutAmount)}
          hint={plural(s.payoutCount || 0, 'payout')}
          loading={loading}
          uppercaseLabel={false}
        />
      </div>

      {showBuyRate ? (
        <div className="border-t border-slate-200 pt-5 dark:border-slate-700">
          <p className="mb-3 text-sm font-medium uppercase text-slate-500 dark:text-slate-400">Reconciliation</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {reconciliation.map((card) => (
              <StatCard
                key={card.key}
                label={card.label}
                value={card.value}
                loading={loading}
                uppercaseLabel={false}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// A plain minus/equals glyph between the pieces of the equation. Decorative
// only — aria-hidden, because the relationship it draws is already carried
// by the cards' own labels and order, not by the character itself.
const Operator = ({ symbol }) => (
  <div
    aria-hidden="true"
    className="flex items-center justify-center py-0.5 text-lg font-semibold text-slate-300 dark:text-slate-600 lg:px-1 lg:py-0"
  >
    {symbol}
  </div>
);

// The deduction list: same card shell as StatCard's own (border, radius,
// shadow, background) so it reads as a sibling of Gross and Net rather than
// a different kind of thing, but built by hand because what it needs to
// express — several labelled rows lining up on a shared right edge — isn't
// a shape StatCard has any way to take.
const DeductionsCard = ({ rows, loading }) => (
  <div className="min-w-0 overflow-hidden rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 sm:p-5 lg:flex-[1.15]">
    <p className="text-sm font-medium uppercase text-slate-500 dark:text-slate-400">Fees</p>
    <div className="mt-2 divide-y divide-slate-200 dark:divide-slate-700">
      {rows.map((row) => (
        <DeductionRow
          key={row.key}
          label={row.label}
          hint={row.hint}
          value={row.value}
          loading={loading}
          credit={row.credit}
        />
      ))}
    </div>
  </div>
);

// Currency figures get tabular numerals, same as the finance tables, so the
// column of amounts lines up down its own right edge. The sign is its own
// element, never concatenated into the amount's text, so a figure that
// hasn't arrived can still be swapped for a skeleton without leaving a bare
// "-" behind.
//
// `credit` rows (feesNotCollected) get a "+" and the app's own positive-tone
// color instead of the neutral minus everything else uses, so the one row
// that adds back to Net can't be mistaken for another deduction at a glance.
const DeductionRow = ({ label, hint, value, loading, credit = false }) => {
  const amountColor = credit ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100';
  const signColor = credit ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500';
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
        {hint ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
      </div>
      <div className={`flex shrink-0 items-baseline gap-1 text-sm font-semibold ${amountColor}`}>
        <span aria-hidden="true" className={signColor}>
          {credit ? '+' : '−'}
        </span>
        {loading ? (
          <SkeletonBlock className="h-4 w-14" />
        ) : (
          <span className="tabular-nums">{formatCurrency(value || 0)}</span>
        )}
      </div>
    </div>
  );
};

export default ConnectFinanceSummary;
